import { forwardRef, type SyntheticEvent, useMemo, useState } from "react";
import { Box, Checkbox, MenuItem, type MenuItemProps, styled, type TextFieldProps } from "~/common/mui";
import { C_ON_SURFACE } from "../icon/Colors";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";
import { TextField } from "~/common/mui/TextField/TextField";
import StarIcon from "@mui/icons-material/Star";

export interface SimpleSelectProps extends Omit<TextFieldProps<"outlined">, "variant"> {
  values: DropdownEntry<string | number>[];
  showFilter?: boolean;
  placeholder?: string;
  displayEmpty?: boolean;
  shrink?: boolean;
  onClose?: (e: SyntheticEvent) => void;
  onOpen?: (e: SyntheticEvent) => void;
  open?: boolean;
  /**
   * Data test id for automation
   */
  "data-test-id"?: string;
}

const SearchBoxContainer = styled(Box)({
  padding: "10px 20px",
  minHeight: "50px",
  color: C_ON_SURFACE,
});

export const SimpleSelectItem = MenuItem;

type MultiSelectMenuItemProps = MenuItemProps & {
  selected?: boolean;
  children?: React.ReactNode;
  value?: string | number;
  disabled?: boolean;
};

export const SimpleMultiSelectItem = styled(
  forwardRef<HTMLLIElement, MultiSelectMenuItemProps>((props: MultiSelectMenuItemProps, ref) => {
    return (
      <MenuItem {...props} ref={ref}>
        <Checkbox checked={props.selected} edge="start" />
        <div>{props.children}</div>
      </MenuItem>
    );
  }),
)({
  display: "flex",
});

/**
 * A wrapper around Material UI TextField components to render a select dropdown.
 *
 * Capabilities:
 * - Can render a search filter inside the dropdown menu (`showFilter` prop).
 * - Can render a star icon next to the label if the entry has `defaultValue: true`.
 */
export const SimpleSelect = forwardRef<HTMLDivElement, SimpleSelectProps>((props: SimpleSelectProps, ref) => {
  const {
    values,
    children,
    placeholder,
    showFilter = false,
    displayEmpty,
    shrink,
    slotProps = {},
    onClose,
    onOpen,
    open,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [filterText, setFilterText] = useState("");
  const elements = useMemo(() => {
    let filteredValues = values;
    if (filterText) {
      filteredValues = values.filter((element) => element.stringLabel?.includes(filterText));
    }
    return createSingleSelectItems(filteredValues, rest["data-test-id"]);
  }, [values, filterText]);

  return (
    <TextField
      select
      variant={"outlined"}
      {...rest}
      slotProps={{
        ...slotProps,
        inputLabel: {
          shrink,
        },
      }}
      /*
       * Unfortunately using slotProps.select.open does not work inside the project for some reason.
       * It DOES work inside storybook though, why?
       */
      SelectProps={{
        onClose,
        onOpen,
        displayEmpty,
        renderValue: (value) => {
          if (value === "" || value === null || value === undefined) {
            return placeholder;
          }
          return values.find((item) => item.value === value)?.label ?? undefined;
        },
        open,
      }}
      ref={ref}
    >
      {showFilter && (
        <SearchBoxContainer>
          <TextField
            placeholder={t("generic.searchHere")}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            value={filterText}
            onClick={PREVENT_CLOSE}
          />
        </SearchBoxContainer>
      )}
      {placeholder && (
        <SimpleSelectItem value={""} data-test-id={rest["data-test-id"] + "-placeholder"}>
          {placeholder}
        </SimpleSelectItem>
      )}
      {elements}
      {children}
    </TextField>
  );
});

export const createSingleSelectItems = (items: DropdownEntry<string | number>[], testId?: string) => {
  const hasDefaultValue = items.some((item) => item.defaultValue !== undefined);

  if (hasDefaultValue) {
    return items.map((item) => {
      const childId = testId ? `${testId}-item` : undefined;
      return (
        <SimpleSelectItem key={item.value} value={item.value} data-test-id={childId}>
          {item.defaultValue ? (
            <StarIcon className={"mr-1"} sx={{ fontSize: 10 }} />
          ) : (
            <div style={{ width: 15 }} />
          )}
          {item.label}
        </SimpleSelectItem>
      );
    });
  }

  return items.map((item) => {
    const childId = testId ? `${testId}-item` : undefined;
    return (
      <SimpleSelectItem key={item.value} value={item.value} data-test-id={childId}>
        {item.label}
      </SimpleSelectItem>
    );
  });
};

const PREVENT_CLOSE = (e: React.MouseEvent) => e.stopPropagation();
